import { Injectable, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';

import { HttpClient, HttpRequest } from '@angular/common/http';
import { Observable, ReplaySubject, of, pipe, forkJoin } from 'rxjs';
import { map, last, catchError } from 'rxjs/operators';

import { inplaceKeySort, generateUUID4 } from './app.utils';
import { SettingsService } from './settings/settings.service';
import {
	Response,
	Game, Team,
	MediaEvent, Flavor,
	Poster, User
} from './app.models';


@Injectable({ providedIn: 'root' })
export class CoreService {

	//Debug Tools: Component and services
	schedule: any = null;
	profile: any = null;
	component_header: any = null;
	component_schedule: any = null;
	component_tvbox: any = null;
	component_poster: any = null;
	component_footer: any = null;


	debug_expose(){
		window['core'] = this;
		window['settings'] = this.settings;
		window['schedule'] = this.schedule;
		window['profile'] = this.profile;
	}

	//The main events bus; push and listen whatever!
	events: EventEmitter<string> = new EventEmitter();
	// paywall -> user hit a paywall
	// profile.auth-required -> action needs user authentication
	// profile.auth-changed -> action needs user authentication
	// report.submitted -> Successfully sent an issue report
	// tvbox.player.playing
	// tvbox.player.crashed




	constructor(
		public settings: SettingsService,
		public http: HttpClient,
		public router: Router
	){
		this.device_detect();

		this.stateshot_sync_timestamp = (new Date()).getTime();
		this.stateshot_parse();

		if( this.is_debug )
			this.debug_expose();
	}


	//Device Info
	device_ios_version: number[] = null;
	device_is_webkit: boolean = false;
	device_uuid: string = null;
	device_detect(){
		if (/iP(hone|od|ad)/.test(navigator.platform)) {
			var v = (navigator.appVersion).match(/OS (\d+)_(\d+)_?(\d+)?/);
			this.device_ios_version = [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3] || '0', 10)];
		}

		if( /AppleWebKit/.test(navigator.userAgent) && (!/Chrome|Chromium|Edg/.test(navigator.userAgent) || /Macintosh/.test(navigator.userAgent)) ){
			//if( navigator.userAgent.indexOf('AppleWebKit') != -1 ){
			this.device_is_webkit = true; //!!(this.device_ios_version);
		}

		this.device_uuid = localStorage['device_uuid'] = localStorage['device_uuid'] || generateUUID4();
	}


	//Site models logic
	get stateshot() { return window['stateshot']; }
	set stateshot(v){ window['stateshot'] = v; }

	stateshot_sync_timestamp = 0; //Current timestamp of the last sync
	stateshot_synced: EventEmitter<null> = new EventEmitter();
	async stateshot_sync(){
		let response = await this.json_request('GET', '/api/v4/stateshot');
		if( !response.ok ){
			console.log( "Something went wrong with stateshot request:", response );
			return;
		}
		this.stateshot = response.json;
		this.stateshot_sync_timestamp = (new Date()).getTime();
		this.stateshot_parse();
	}
	stateshot_parse(){
		this.stateshot_update_and_merge_model_list( Team, this.teams, this.stateshot['teams'] );

		this.stateshot_update_and_merge_model_list( Game, this.games, this.stateshot['games'] );
		inplaceKeySort( this.games, (g) => g.datetime );

		this.stateshot_update_and_merge_model_list( MediaEvent, this.media_events, this.stateshot['media_events'] );
		inplaceKeySort( this.media_events, (g) => g.datetime );

		this.stateshot_update_and_merge_model_list( Flavor, this.flavors, this.stateshot['flavors'] );

		this.stateshot_update_and_merge_model_list( Poster, this.posters, this.stateshot['posters'] );
		inplaceKeySort( this.posters, (p) => p.order );

		const meta = this.stateshot['meta'];

		this.is_debug = ( meta['stage'] == 'alpha' );
		this.is_beta = ( meta['stage'] == 'beta' );
		this.is_premium = !!( meta['entitlements'] && meta['entitlements'].length > 0 );
		this.sport = meta['sports'].toLowerCase();

		this.site_logo = this.is_premium ? '/sport-assets/logo24.svg' : '/sport-assets/logo.svg';

		let comp = this.stateshot['comp_prediction'];
		this.is_prediction_active = false;
		if( comp ){
			this.is_prediction_active = true;
			this.stateshot_update_and_merge_model_list( User, this.top_prediction_users, comp['leaderboard'] );

			this.prediction_recent_users = 10;
			this.prediction_active_users = 200;
		}

		comp = this.stateshot['comp_betting'];
		if( comp ){
			this.stateshot_update_and_merge_model_list( User, this.top_simulator_users, comp['leaderboard'] );
		}

		this.stateshot_synced.emit();
	}
	stateshot_update_and_merge_model_list( Model, current_list, new_list ){
		for( let m of new_list ){
			let model = Model.INSTANCES[m['id']];
			if( model ) model.update(m, this.stateshot_sync_timestamp);
			else current_list.push(new Model(m, this.stateshot_sync_timestamp));
		}

		for( let i = 0; i < current_list.length; i++ ){
			let model = current_list[i];
			model.sync_outdated = model.sync_timestamp < this.stateshot_sync_timestamp;
			if( !model.sync_outdated ) continue

			current_list.splice(i, 1); i--; //Remove this element, and moveback the pointer;
		}
	}


	//Stateshot: Customized Values
	is_premium: boolean; //Wheter this is a premium only site or not.
	is_debug: boolean;
	is_beta: boolean;
	sport: string;
	site_logo: string;

	//Stateshot: Important and globaly needed models
	games: Game[] = [];
	teams: Team[] = [];
	media_events: MediaEvent[] = [];
	flavors: Flavor[] = [];
	posters: Poster[] = [];

	is_prediction_active: boolean = false;
	top_prediction_users: any[] = [];
	top_simulator_users: any[] = [];

	prediction_recent_users: number = null;
	prediction_active_users: number = null;

	//Deprecated And Transition patches
	get user() { return this.profile.user; }


	//Load Custom Libraries, Ex: await load_scripts('assets/test.js').toPromise();
	loaded_scripts: any = {}; //{ [url: string]: ReplaySubject<any> } = {};
	load_scripts(...urls: string[]): Observable<any> {
		return forkJoin(urls.map(url=>{return this.load_script(url)}));
	}
	load_script(url: string): Observable<any> {
		if (this.loaded_scripts[url]) {
			return this.loaded_scripts[url].asObservable();
		}

		this.loaded_scripts[url] = new ReplaySubject();

		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.async = true;
		script.src = url;
		script.onload = () => {
			this.loaded_scripts[url].next(null);
			this.loaded_scripts[url].complete();
		};

		document.body.appendChild(script);

		return this.loaded_scripts[url].asObservable();
	}
	json_request( ...args: any[] ){
		try { new URL(args[1]); }
		catch(e){ args[1] = `https://api.${this.top_domain}${args[1]}`; }

		args.unshift(null);
		let raw_request = new (Function.prototype.bind['apply' + ''](HttpRequest, args));
		return this.http.request( raw_request ).pipe(
			last(),
			catchError( (error: any) => {
				let r = new Response(error);
				return of(r);
			}),
			map( (response: any) => {
				if( response.error_type ) return response;
				let r = new Response(response);
				return r;
			}),
		).toPromise();
	}


	//Routing Tools
	top_domain: string = location.hostname.split(".").slice(-2).join(".")
	get url_path(): string { return window.location.pathname; }
	redirectTo( u, scroll = false ){
		if( typeof(u) == "string" ) u = [u];
		this.router.navigateByUrl('/', {skipLocationChange: true}).then(()=>{
			this.router.navigate(u)

			if( scroll )
				setTimeout(()=>{
					window.scrollTo({ top: document.getElementsByTagName('router-outlet')[0]['offsetTop'] - 50, behavior: 'smooth' })
				}, 10);
		});
	}

}
